import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { contractConfigApi } from '../services/api';

interface FeeTier {
  min_sek: number;
  max_sek: number | null;
  fee_pct: number;
}

interface ContractConfig {
  id: string;
  name: string;
  description?: string;
  is_default: boolean;
  customer_type: 'private' | 'business' | 'partner';
  fee_tiers: FeeTier[];
  payment_method_fees?: {
    bank?: number | null;
    card?: number | null;
    swish?: number | null;
  };
  project_fee_per_party: number;
  min_fee_per_party: number;
  customer_pays_transaction: boolean;
  company_pays_transaction: boolean;
  customer_pays_project_fee: boolean;
  company_pays_project_fee: boolean;
  is_partner_subscription: boolean;
  annual_subscription_fee?: number;
  partner_covers_customer_fee: boolean;
  etappio_cost_per_transaction: number;
  etappio_target_margin_pct: number;
  created_at: string;
  updated_at: string;
}

interface SimulationResult {
  paymentMethod: 'bank' | 'swish' | 'card';
  transactionFee: number;
  etappioVariableFee: number;
  paymentMethodFee: number;
  paymentMethodFeePct: number;
  projectFee: number;
  oneTimeFeesApplied: boolean;
  customerFee: number;
  companyFee: number;
  etappioRevenue: number;
  etappioCost: number;
  etappioMargin: number;
  etappioMarginPct: number;
  breakdown: {
    transactionFee: number;
    projectFee: number;
    customerMinimumFeeAdjustment?: number;
    companyMinimumFeeAdjustment?: number;
    etappioVariableFee: number;
    paymentMethodFee: number;
    applicableTier: FeeTier | null;
  };
}

const emptyConfig: Omit<ContractConfig, 'id' | 'created_at' | 'updated_at'> = {
  name: '',
  description: '',
  is_default: false,
  customer_type: 'private',
  fee_tiers: [{ min_sek: 0, max_sek: null, fee_pct: 2.0 }],
  payment_method_fees: { bank: 1.5, card: 2.0, swish: 1.0 },
  project_fee_per_party: 495,
  min_fee_per_party: 495,
  customer_pays_transaction: true,
  company_pays_transaction: true,
  customer_pays_project_fee: true,
  company_pays_project_fee: true,
  is_partner_subscription: false,
  annual_subscription_fee: 0,
  partner_covers_customer_fee: false,
  etappio_cost_per_transaction: 50,
  etappio_target_margin_pct: 40
};

export function AdminContractConfig() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ContractConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [formData, setFormData] = useState<any>({ ...emptyConfig });

  // Simulator state
  const [simConfigId, setSimConfigId] = useState('');
  const [simPaymentAmount, setSimPaymentAmount] = useState<number>(500000);
  const [simPaymentMethod, setSimPaymentMethod] = useState<'bank' | 'swish' | 'card'>('bank');
  const [simIncludeOneTimeFees, setSimIncludeOneTimeFees] = useState(true);
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);

  const loadConfigs = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await contractConfigApi.getAll();
      setConfigs(data);
      if (data.length > 0 && !simConfigId) {
        setSimConfigId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load configs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [simConfigId]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const openCreateModal = () => {
    setEditingConfig(null);
    setFormData({ ...emptyConfig });
    setShowModal(true);
  };

  const openEditModal = (config: ContractConfig) => {
    setEditingConfig(config);
    setFormData({ ...config, payment_method_fees: config.payment_method_fees || {} });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        payment_method_fees: {
          bank: formData.payment_method_fees?.bank ?? null,
          card: formData.payment_method_fees?.card ?? null,
          swish: formData.payment_method_fees?.swish ?? null
        }
      };
      if (editingConfig) {
        await contractConfigApi.update(editingConfig.id, payload);
      } else {
        await contractConfigApi.create(payload);
      }
      setShowModal(false);
      loadConfigs();
    } catch (error) {
      console.error('Failed to save config:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('adminContractConfig.confirmDelete'))) return;
    try {
      await contractConfigApi.delete(id);
      loadConfigs();
    } catch (error) {
      console.error('Failed to delete config:', error);
    }
  };

  const handleSimulate = async () => {
    if (!simConfigId || simPaymentAmount <= 0) return;
    try {
      setSimLoading(true);
      const result = await contractConfigApi.simulate({
        projectValue: simPaymentAmount,
        configId: simConfigId,
        paymentMethod: simPaymentMethod,
        includeOneTimeFees: simIncludeOneTimeFees
      });
      setSimResult(result);
    } catch (error) {
      console.error('Simulation failed:', error);
    } finally {
      setSimLoading(false);
    }
  };

  const updateTier = (index: number, field: keyof FeeTier, value: any) => {
    const tiers = [...formData.fee_tiers];
    tiers[index] = { ...tiers[index], [field]: value };
    setFormData({ ...formData, fee_tiers: tiers });
  };

  const addTier = () => {
    const tiers = [...formData.fee_tiers];
    const lastMax = tiers.length > 0 ? (tiers[tiers.length - 1].max_sek || 0) : 0;
    tiers.push({ min_sek: lastMax + 1, max_sek: null, fee_pct: 1.5 });
    setFormData({ ...formData, fee_tiers: tiers });
  };

  const removeTier = (index: number) => {
    const tiers = formData.fee_tiers.filter((_: any, i: number) => i !== index);
    setFormData({ ...formData, fee_tiers: tiers });
  };

  const formatSEK = (value: number) =>
    new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 0 }).format(value);

  const customerTypeLabel = (type: string) => {
    switch (type) {
      case 'private': return t('adminContractConfig.custPrivate');
      case 'business': return t('adminContractConfig.custBusiness');
      case 'partner': return t('adminContractConfig.custPartner');
      default: return type;
    }
  };

  const selectedConfig = configs.find(c => c.id === simConfigId);

  return (
    <DashboardLayout>
      <div className="page-header">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="m-b-10">{t('adminContractConfig.pageTitle')}</h5>
            <Breadcrumb
              items={[
                { label: t('admin.nav.home'), path: '/admin/home' },
                { label: t('admin.nav.system'), path: '#' },
                { label: t('adminContractConfig.pageTitle') }
              ]}
            />
          </div>
        </div>
      </div>

      {/* Section 1: Configurations table */}
      <Card title={t('adminContractConfig.configsTitle')} headerAction={
        <button className="btn btn-sm btn-primary" onClick={openCreateModal}>
          <i className="feather-plus me-1"></i>{t('adminContractConfig.createNew')}
        </button>
      }>
        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : configs.length === 0 ? (
          <p className="text-muted text-center py-4">{t('adminContractConfig.emptyState')}</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{t('adminContractConfig.colName')}</th>
                  <th>{t('adminContractConfig.colType')}</th>
                  <th>{t('adminContractConfig.colTransactionFee')}</th>
                  <th>{t('adminContractConfig.colProjectFee')}</th>
                  <th>{t('adminContractConfig.colMinFeeTotal')}</th>
                  <th>{t('adminContractConfig.defaultLabel')}</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {configs.map(config => {
                  const feeRange = config.fee_tiers.length > 0
                    ? `${Math.min(...config.fee_tiers.map(t => t.fee_pct))}% - ${Math.max(...config.fee_tiers.map(t => t.fee_pct))}%`
                    : '-';
                  return (
                    <tr key={config.id}>
                      <td className="fw-semibold">{config.name}</td>
                      <td><span className="badge bg-soft-primary text-primary">{customerTypeLabel(config.customer_type)}</span></td>
                      <td>{feeRange}</td>
                      <td>{formatSEK(config.project_fee_per_party)}</td>
                      <td>{formatSEK(config.min_fee_per_party)}</td>
                      <td>
                        {config.is_default && <span className="badge bg-success">{t('adminContractConfig.defaultLabel')}</span>}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-icon btn-light me-1" onClick={() => openEditModal(config)} title={t('adminContractConfig.editAction')}>
                          <i className="feather-edit-2"></i>
                        </button>
                        <button className="btn btn-sm btn-icon btn-light text-danger" onClick={() => handleDelete(config.id)} title={t('adminContractConfig.deleteAction')}>
                          <i className="feather-trash-2"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Section 2: Simulator */}
      <Card title={t('adminContractConfig.simTitle')} className="mt-4">
        <div className="row g-3 align-items-end mb-4">
          <div className="col-md-3">
            <label className="form-label">{t('adminContractConfig.simConfigLabel')}</label>
            <select
              className="form-select"
              value={simConfigId}
              onChange={e => {
                setSimConfigId(e.target.value);
                setSimResult(null);
              }}
            >
              <option value="">{t('adminContractConfig.simConfigPlaceholder')}</option>
              {configs.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({customerTypeLabel(c.customer_type)})</option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('adminContractConfig.simPaymentAmountLabel')}</label>
            <input
              type="number"
              className="form-control"
              value={simPaymentAmount}
              onChange={e => {
                setSimPaymentAmount(Number(e.target.value));
                setSimResult(null);
              }}
              min={0}
              step={10000}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">{t('adminContractConfig.simPaymentMethodLabel')}</label>
            <select
              className="form-select"
              value={simPaymentMethod}
              onChange={e => {
                setSimPaymentMethod(e.target.value as 'bank' | 'swish' | 'card');
                setSimResult(null);
              }}
            >
              <option value="bank">{t('adminContractConfig.simMethodBank')}</option>
              <option value="swish">{t('adminContractConfig.simMethodSwish')}</option>
              <option value="card">{t('adminContractConfig.simMethodCard')}</option>
            </select>
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100" onClick={handleSimulate} disabled={simLoading || !simConfigId || simPaymentAmount <= 0}>
              {simLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="feather-play me-1"></i>}
              {t('adminContractConfig.simCalculate')}
            </button>
          </div>
          <div className="col-12">
            <div className="form-check">
              <input
                id="sim-include-one-time-fees"
                className="form-check-input"
                type="checkbox"
                checked={simIncludeOneTimeFees}
                onChange={e => {
                  setSimIncludeOneTimeFees(e.target.checked);
                  setSimResult(null);
                }}
              />
              <label className="form-check-label" htmlFor="sim-include-one-time-fees">
                {t('adminContractConfig.simIncludeOneTimeFees')}
              </label>
            </div>
          </div>
        </div>

        {simResult && (
          <div className="row g-3">
            <div className="col-md-4">
              <div className="card border-0 bg-soft-info mb-0">
                <div className="card-body text-center">
                  <div className="fs-12 text-muted mb-1">{t('adminContractConfig.resCustomerFee')}</div>
                  <div className="fs-4 fw-bold text-info">{formatSEK(simResult.customerFee)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 bg-soft-warning mb-0">
                <div className="card-body text-center">
                  <div className="fs-12 text-muted mb-1">{t('adminContractConfig.resCompanyFee')}</div>
                  <div className="fs-4 fw-bold text-warning">{formatSEK(simResult.companyFee)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 bg-soft-primary mb-0">
                <div className="card-body text-center">
                  <div className="fs-12 text-muted mb-1">{t('adminContractConfig.resEtappioRevenue')}</div>
                  <div className="fs-4 fw-bold text-primary">{formatSEK(simResult.etappioRevenue)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 bg-soft-secondary mb-0">
                <div className="card-body text-center">
                  <div className="fs-12 text-muted mb-1">{t('adminContractConfig.resEtappioCost')}</div>
                  <div className="fs-4 fw-bold text-secondary">{formatSEK(simResult.etappioCost)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 bg-soft-success mb-0">
                <div className="card-body text-center">
                  <div className="fs-12 text-muted mb-1">{t('adminContractConfig.resEtappioMargin')}</div>
                  <div className="fs-4 fw-bold text-success">{formatSEK(simResult.etappioMargin)}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 bg-soft-success mb-0">
                <div className="card-body text-center">
                  <div className="fs-12 text-muted mb-1">{t('adminContractConfig.resMarginPct')}</div>
                  <div className="fs-4 fw-bold text-success">{simResult.etappioMarginPct.toFixed(1)}%</div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="alert alert-light mb-0">
                <small>
                  {simResult.breakdown.applicableTier && (
                    <>
                      <strong>{t('adminContractConfig.bdApplicableTier')}</strong> {formatSEK(simResult.breakdown.applicableTier.min_sek)}
                      {simResult.breakdown.applicableTier.max_sek ? ` - ${formatSEK(simResult.breakdown.applicableTier.max_sek)}` : '+'} @ {simResult.breakdown.applicableTier.fee_pct}%
                      {' | '}
                    </>
                  )}
                  <strong>{t('adminContractConfig.bdEtappioVariableFee')}</strong> {formatSEK(simResult.breakdown.etappioVariableFee)}
                  {' | '}
                  <strong>{t('adminContractConfig.bdPaymentMethodFee')}</strong> {formatSEK(simResult.breakdown.paymentMethodFee)} ({simResult.paymentMethodFeePct}%)
                  {' | '}
                  <strong>{t('adminContractConfig.bdTransactionFee')}</strong> {formatSEK(simResult.breakdown.transactionFee)}
                  {' | '}
                  <strong>{t('adminContractConfig.bdProjectFee')}</strong> {formatSEK(simResult.breakdown.projectFee)}
                  {Number(simResult.breakdown.customerMinimumFeeAdjustment || 0) > 0 && (
                    <>
                      {' | '}
                      <strong>{t('adminContractConfig.bdCustomerMinAdj')}</strong> {formatSEK(simResult.breakdown.customerMinimumFeeAdjustment || 0)}
                    </>
                  )}
                  {Number(simResult.breakdown.companyMinimumFeeAdjustment || 0) > 0 && (
                    <>
                      {' | '}
                      <strong>{t('adminContractConfig.bdCompanyMinAdj')}</strong> {formatSEK(simResult.breakdown.companyMinimumFeeAdjustment || 0)}
                    </>
                  )}
                </small>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Section 3: Fee tier visualization */}
      {selectedConfig && (
        <Card title={t('adminContractConfig.feeTiersTitle', { name: selectedConfig.name })} className="mt-4">
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>{t('adminContractConfig.colMinimum')}</th>
                  <th>{t('adminContractConfig.colMaximum')}</th>
                  <th>{t('adminContractConfig.colFeePct')}</th>
                </tr>
              </thead>
              <tbody>
                {[...selectedConfig.fee_tiers]
                  .sort((a, b) => a.min_sek - b.min_sek)
                  .map((tier, idx) => (
                    <tr key={idx}>
                      <td>{formatSEK(tier.min_sek)}</td>
                      <td>{tier.max_sek !== null ? formatSEK(tier.max_sek) : <em className="text-muted">{t('adminContractConfig.noLimit')}</em>}</td>
                      <td><span className="badge bg-primary">{tier.fee_pct}%</span></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Modal for create/edit */}
      {showModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingConfig ? t('adminContractConfig.modalEditTitle') : t('adminContractConfig.modalCreateTitle')}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('adminContractConfig.fieldNameLabel')}</label>
                      <input className="form-control" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('adminContractConfig.fieldCustomerType')}</label>
                      <select className="form-select" value={formData.customer_type} onChange={e => setFormData({ ...formData, customer_type: e.target.value })}>
                        <option value="private">{t('adminContractConfig.custPrivate')}</option>
                        <option value="business">{t('adminContractConfig.custBusiness')}</option>
                        <option value="partner">{t('adminContractConfig.custPartner')}</option>
                      </select>
                    </div>
                    <div className="col-md-3 d-flex align-items-end">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={formData.is_default} onChange={e => setFormData({ ...formData, is_default: e.target.checked })} id="isDefault" />
                        <label className="form-check-label" htmlFor="isDefault">{t('adminContractConfig.defaultLabel')}</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">{t('adminContractConfig.fieldDescription')}</label>
                      <textarea className="form-control" rows={2} value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} />
                    </div>

                    <div className="col-12"><hr className="my-1" /><h6 className="mb-0">{t('adminContractConfig.feeTiersHeading')}</h6></div>
                    {formData.fee_tiers.map((tier: FeeTier, idx: number) => (
                      <div className="col-12" key={idx}>
                        <div className="row g-2 align-items-end">
                          <div className="col">
                            <label className="form-label fs-12">{t('adminContractConfig.tierMin')}</label>
                            <input type="number" className="form-control form-control-sm" value={tier.min_sek} onChange={e => updateTier(idx, 'min_sek', Number(e.target.value))} />
                          </div>
                          <div className="col">
                            <label className="form-label fs-12">{t('adminContractConfig.tierMax')}</label>
                            <input type="number" className="form-control form-control-sm" value={tier.max_sek ?? ''} placeholder={t('adminContractConfig.noLimit')} onChange={e => updateTier(idx, 'max_sek', e.target.value === '' ? null : Number(e.target.value))} />
                          </div>
                          <div className="col">
                            <label className="form-label fs-12">{t('adminContractConfig.colFeePct')}</label>
                            <input type="number" step="0.1" className="form-control form-control-sm" value={tier.fee_pct} onChange={e => updateTier(idx, 'fee_pct', Number(e.target.value))} />
                          </div>
                          <div className="col-auto">
                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeTier(idx)} disabled={formData.fee_tiers.length <= 1}>
                              <i className="feather-trash-2"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="col-12">
                      <button className="btn btn-sm btn-outline-primary" onClick={addTier}>
                        <i className="feather-plus me-1"></i>{t('adminContractConfig.addTier')}
                      </button>
                    </div>

                    <div className="col-12"><hr className="my-1" /><h6 className="mb-0">{t('adminContractConfig.methodFees.title', 'Transaktionsavgift per betalmetod')}</h6></div>
                    <div className="col-12">
                      <p className="text-muted fs-12 mb-2">{t('adminContractConfig.methodFees.help', 'Anges i procent. Läggs ovanpå avgiftsstegen för den betalmetod beställaren väljer och beräknas på basbelopp plus övriga avgifter. Lämna tomt för att bara använda avgiftsstegen.')}</p>
                    </div>
                    {(['bank', 'card', 'swish'] as const).map(mk => (
                      <div className="col-md-4" key={mk}>
                        <label className="form-label">
                          {mk === 'bank'
                            ? t('adminContractConfig.methodFees.bank', 'Trustly (%)')
                            : mk === 'card'
                              ? t('adminContractConfig.methodFees.card', 'Kort (%)')
                              : t('adminContractConfig.methodFees.swish', 'Swish (%)')}
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className="form-control"
                          placeholder={t('adminContractConfig.methodFees.placeholder', 'Ej satt')}
                          value={formData.payment_method_fees?.[mk] ?? ''}
                          onChange={e => setFormData({
                            ...formData,
                            payment_method_fees: {
                              ...(formData.payment_method_fees || {}),
                              [mk]: e.target.value === '' ? null : Number(e.target.value)
                            }
                          })}
                        />
                      </div>
                    ))}

                    <div className="col-12"><hr className="my-1" /><h6 className="mb-0">{t('adminContractConfig.fixedFeesHeading')}</h6></div>
                    <div className="col-12">
                      <p className="text-muted fs-12 mb-0">{t('adminContractConfig.fixedFeesHelp')}</p>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('adminContractConfig.fieldProjectFeePerParty')}</label>
                      <input type="number" className="form-control" value={formData.project_fee_per_party} onChange={e => setFormData({ ...formData, project_fee_per_party: Number(e.target.value) })} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('adminContractConfig.fieldMinFeePerParty')}</label>
                      <input type="number" className="form-control" value={formData.min_fee_per_party} onChange={e => setFormData({ ...formData, min_fee_per_party: Number(e.target.value) })} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('adminContractConfig.fieldEtappioCost')}</label>
                      <input type="number" className="form-control" value={formData.etappio_cost_per_transaction} onChange={e => setFormData({ ...formData, etappio_cost_per_transaction: Number(e.target.value) })} />
                    </div>

                    <div className="col-12"><hr className="my-1" /><h6 className="mb-0">{t('adminContractConfig.distributionHeading')}</h6></div>
                    <div className="col-12">
                      <p className="text-muted fs-12 mb-2">{t('adminContractConfig.distributionHelp')}</p>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" checked={formData.customer_pays_transaction} onChange={e => setFormData({ ...formData, customer_pays_transaction: e.target.checked })} id="custPaysTx" />
                        <label className="form-check-label" htmlFor="custPaysTx">{t('adminContractConfig.custPaysTx')}</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={formData.customer_pays_project_fee} onChange={e => setFormData({ ...formData, customer_pays_project_fee: e.target.checked })} id="custPaysPrj" />
                        <label className="form-check-label" htmlFor="custPaysPrj">{t('adminContractConfig.custPaysPrj')}</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" checked={formData.company_pays_transaction} onChange={e => setFormData({ ...formData, company_pays_transaction: e.target.checked })} id="compPaysTx" />
                        <label className="form-check-label" htmlFor="compPaysTx">{t('adminContractConfig.compPaysTx')}</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={formData.company_pays_project_fee} onChange={e => setFormData({ ...formData, company_pays_project_fee: e.target.checked })} id="compPaysPrj" />
                        <label className="form-check-label" htmlFor="compPaysPrj">{t('adminContractConfig.compPaysPrj')}</label>
                      </div>
                    </div>

                    <div className="col-12"><hr className="my-1" /><h6 className="mb-0">{t('adminContractConfig.partnerHeading')}</h6></div>
                    <div className="col-md-4">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={formData.is_partner_subscription} onChange={e => setFormData({ ...formData, is_partner_subscription: e.target.checked })} id="isPartnerSub" />
                        <label className="form-check-label" htmlFor="isPartnerSub">{t('adminContractConfig.partnerSubscription')}</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">{t('adminContractConfig.annualSubscriptionFee')}</label>
                      <input type="number" className="form-control" value={formData.annual_subscription_fee || 0} onChange={e => setFormData({ ...formData, annual_subscription_fee: Number(e.target.value) })} />
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={formData.partner_covers_customer_fee} onChange={e => setFormData({ ...formData, partner_covers_customer_fee: e.target.checked })} id="partnerCovers" />
                        <label className="form-check-label" htmlFor="partnerCovers">{t('adminContractConfig.partnerCoversCustomerFee')}</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">{t('adminContractConfig.targetMarginPct')}</label>
                      <input type="number" step="1" className="form-control" value={formData.etappio_target_margin_pct} onChange={e => setFormData({ ...formData, etappio_target_margin_pct: Number(e.target.value) })} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('adminContractConfig.cancel')}</button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={!formData.name}>
                    {editingConfig ? t('adminContractConfig.save') : t('adminContractConfig.create')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
